import Papa from 'papaparse';
import type { Curve, ParsedData } from '../../../interfaces/toolData';
import { cleanTimeSeriesData } from '../../dataQuality';

/**
 * Timestamp column detection result
 */
interface TimestampColumnInfo {
  type: 'single' | 'dual';
  dateColumn?: string;
  timeColumn?: string;
  datetimeColumn?: string;
  format: string;
}

/**
 * Generic CSV Parser - Fallback parser for unrecognized CSV files
 * 
 * Strict Requirements:
 * - No metadata section (first row = column headers)
 * - 1-2 detectable timestamp columns
 * - Remaining columns = numeric time series data
 * - No obvious manufacturer signatures
 * 
 * Supports common timestamp formats:
 * - Dual column: YYYY/MM/DD + HH:MM:SS
 * - Single column: YYYY-MM-DD HH:MM:SS, etc.
 */
export class GenericCsvParser {
  
  /**
   * Check if content appears to be a valid generic CSV file
   */
  static canParse(content: string): boolean {
    try {
      // Basic structure validation
      if (!content || !content.trim()) {
        return false;
      }
      
      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        return false; // Need at least header + 1 data row
      }
      
      // Reject files with obvious manufacturer signatures
      if (this.hasManufacturerSignature(content)) {
        return false;
      }
      
      // Reject files with metadata sections (must start with headers)
      if (this.hasMetadataSection(lines)) {
        return false;
      }
      
      // Parse CSV structure
      const parseResult = Papa.parse(content, {
        header: true,
        skipEmptyLines: true,
        preview: 5 // Only check first few rows for validation
      });
      
      if (parseResult.errors.length > 0) {
        return false; // Invalid CSV structure
      }
      
      const data = parseResult.data as Record<string, string>[];
      if (data.length === 0) {
        return false;
      }
      
      // Must have at least 3 columns (1-2 timestamps + 1+ data)
      const headers = Object.keys(data[0]);
      if (headers.length < 3) {
        return false;
      }
      
      // Must have detectable timestamp columns
      const timestampInfo = this.detectTimestampColumns(headers, data);
      if (!timestampInfo) {
        return false;
      }
      
      // Must have at least one numeric data column
      const dataColumns = this.getDataColumns(headers, timestampInfo);
      if (dataColumns.length === 0) {
        return false;
      }
      
      // Validate that data columns contain numeric values
      if (!this.validateDataColumns(data, dataColumns)) {
        return false;
      }
      
      return true;
      
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Parse generic CSV content into standardized format
   */
  static parse(content: string, filename: string = ''): ParsedData {
    try {
      // Validate content first
      if (!this.canParse(content)) {
        throw new Error('File does not meet generic CSV requirements');
      }
      
      // Parse CSV data
      const parseResult = Papa.parse(content, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false // Keep as strings for consistent processing
      });
      
      if (parseResult.errors.length > 0) {
        console.warn('CSV parsing warnings:', parseResult.errors);
      }
      
      const data = parseResult.data as Record<string, string>[];
      const headers = Object.keys(data[0]);
      
      // Detect timestamp configuration
      const timestampInfo = this.detectTimestampColumns(headers, data);
      if (!timestampInfo) {
        throw new Error('Could not detect valid timestamp columns');
      }
      
      // Generate time series curves
      const curves = this.generateTimeSeriesCurves(data, timestampInfo);
      
      // Extract basic metadata
      const metadata = this.extractMetadata(filename, data.length, headers.length);
      
      return {
        fileType: 'GENERIC_CSV_TIMESERIES',
        toolType: 'Generic',
        curves,
        metadata,
        errors: parseResult.errors.map(err => err.message)
      };
      
    } catch (error) {
      throw new Error(`Failed to parse generic CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Check for obvious manufacturer signatures that would disqualify generic parsing
   */
  private static hasManufacturerSignature(content: string): boolean {
    const signatures = [
      'MicroPulse',
      'Cortex',
      'Pason PENLESS Drilling Recorder',
      'SmartDM',
      'D-Tech',
      'Eclipse Touch',
      'Innova',
      'Vertex',
      'RIME',
      'ProPulser'
    ];
    
    const upperContent = content.toUpperCase();
    return signatures.some(sig => upperContent.includes(sig.toUpperCase()));
  }
  
  /**
   * Check if file has metadata section before headers
   */
  private static hasMetadataSection(lines: string[]): boolean {
    // Generic CSV must start with column headers (no metadata section)
    // Check if first line looks like a header row by examining if it contains common separators
    const firstLine = lines[0];
    
    // If first line has very few commas/separators, it might be metadata
    const separatorCount = (firstLine.match(/,/g) || []).length;
    if (separatorCount < 2) {
      return true; // Likely metadata, not headers
    }
    
    // Check for common metadata patterns
    const metadataPatterns = [
      /^#/,           // Comments
      /^\/\//,        // Comments  
      /^[A-Z\s]+:$/,  // Key: Value patterns
      /^Version/i,    // Version info
      /^Export/i,     // Export info
      /^Report/i      // Report info
    ];
    
    return metadataPatterns.some(pattern => pattern.test(firstLine));
  }
  
  /**
   * Detect timestamp column configuration
   */
  private static detectTimestampColumns(headers: string[], data: Record<string, string>[]): TimestampColumnInfo | null {
    // Try dual column detection first (date + time)
    const dualResult = this.detectDualTimestampColumns(headers, data);
    if (dualResult) {
      return dualResult;
    }
    
    // Try single datetime column detection
    const singleResult = this.detectSingleTimestampColumn(headers, data);
    if (singleResult) {
      return singleResult;
    }
    
    return null;
  }
  
  /**
   * Detect dual timestamp columns (separate date and time)
   */
  private static detectDualTimestampColumns(headers: string[], data: Record<string, string>[]): TimestampColumnInfo | null {
    // Look for common date/time column name patterns
    const datePatterns = [/date/i, /yyyy/i, /mm.*dd/i, /^yyyy\/mm\/dd$/i];
    const timePatterns = [/time/i, /hh.*mm/i, /hour/i, /^hh:mm:ss$/i];
    
    let dateColumn: string | undefined;
    let timeColumn: string | undefined;
    
    // Find date column
    for (const header of headers) {
      if (datePatterns.some(pattern => pattern.test(header))) {
        // Validate by trying to parse a sample value
        const sampleValue = data[0]?.[header];
        if (sampleValue && this.isValidDateString(sampleValue)) {
          dateColumn = header;
          break;
        }
      }
    }
    
    // Find time column
    for (const header of headers) {
      if (timePatterns.some(pattern => pattern.test(header))) {
        // Validate by trying to parse a sample value
        const sampleValue = data[0]?.[header];
        if (sampleValue && this.isValidTimeString(sampleValue)) {
          timeColumn = header;
          break;
        }
      }
    }
    
    if (dateColumn && timeColumn) {
      return {
        type: 'dual',
        dateColumn,
        timeColumn,
        format: 'YYYY/MM/DD + HH:MM:SS'
      };
    }
    
    return null;
  }
  
  /**
   * Detect single datetime column
   */
  private static detectSingleTimestampColumn(headers: string[], data: Record<string, string>[]): TimestampColumnInfo | null {
    const datetimePatterns = [/timestamp/i, /datetime/i, /time/i];
    
    for (const header of headers) {
      if (datetimePatterns.some(pattern => pattern.test(header))) {
        // Validate by trying to parse a sample value
        const sampleValue = data[0]?.[header];
        if (sampleValue && this.isValidDateTimeString(sampleValue)) {
          return {
            type: 'single',
            datetimeColumn: header,
            format: 'ISO DateTime'
          };
        }
      }
    }
    
    return null;
  }
  
  /**
   * Get data columns (excluding timestamp columns)
   */
  private static getDataColumns(headers: string[], timestampInfo: TimestampColumnInfo): string[] {
    const excludeColumns = new Set<string>();
    
    if (timestampInfo.type === 'dual') {
      if (timestampInfo.dateColumn) excludeColumns.add(timestampInfo.dateColumn);
      if (timestampInfo.timeColumn) excludeColumns.add(timestampInfo.timeColumn);
    } else {
      if (timestampInfo.datetimeColumn) excludeColumns.add(timestampInfo.datetimeColumn);
    }
    
    return headers.filter(header => !excludeColumns.has(header));
  }
  
  /**
   * Validate that data columns contain primarily numeric values
   */
  private static validateDataColumns(data: Record<string, string>[], dataColumns: string[]): boolean {
    // Check first few rows to see if columns are numeric
    const sampleRows = data.slice(0, Math.min(5, data.length));
    
    // Need at least one numeric column
    let numericColumnCount = 0;
    
    for (const column of dataColumns) {
      let numericCount = 0;
      
      for (const row of sampleRows) {
        const value = row[column]?.trim();
        // Handle empty quotes and empty strings
        if (value && value !== '""' && value !== '' && !isNaN(parseFloat(value))) {
          numericCount++;
        }
      }
      
      // If at least 60% of sample values are numeric, consider this a numeric column
      if (numericCount / sampleRows.length >= 0.6) {
        numericColumnCount++;
      }
    }
    
    // Need at least one numeric column to be valid
    return numericColumnCount > 0;
  }
  
  /**
   * Generate time series curves from parsed data
   */
  private static generateTimeSeriesCurves(data: Record<string, string>[], timestampInfo: TimestampColumnInfo): Curve[] {
    const curves: Curve[] = [];
    
    // Parse timestamps
    const timestamps: Date[] = [];
    const validRows: Record<string, string>[] = [];
    
    for (const row of data) {
      let timestamp: Date | null = null;
      
      if (timestampInfo.type === 'dual') {
        const dateStr = row[timestampInfo.dateColumn!]?.trim();
        const timeStr = row[timestampInfo.timeColumn!]?.trim();
        
        if (dateStr && timeStr) {
          timestamp = this.parseDualTimestamp(dateStr, timeStr);
        }
      } else {
        const datetimeStr = row[timestampInfo.datetimeColumn!]?.trim();
        if (datetimeStr) {
          timestamp = this.parseSingleTimestamp(datetimeStr);
        }
      }
      
      if (timestamp && this.isValidTimestamp(timestamp)) {
        timestamps.push(timestamp);
        validRows.push(row);
      }
    }
    
    if (timestamps.length === 0) {
      throw new Error('No valid timestamp data found');
    }
    
    // Create curves for data columns
    const dataColumns = this.getDataColumns(Object.keys(data[0]), timestampInfo);
    
    for (const columnName of dataColumns) {
      const values: number[] = [];
      const curveTimestamps: Date[] = [];
      
      validRows.forEach((row, index) => {
        const valueStr = row[columnName]?.trim();
        // Skip empty values, empty quotes, and non-numeric values
        if (valueStr && valueStr !== '""' && valueStr !== '') {
          const value = parseFloat(valueStr);
          if (!isNaN(value) && isFinite(value)) {
            values.push(value);
            curveTimestamps.push(timestamps[index]);
          }
        }
      });
      
      // Only create curve if we have sufficient numeric data (at least 10% of total rows)
      if (values.length > 0 && values.length >= validRows.length * 0.1) {
        const curve: Curve = {
          name: columnName,
          xValues: curveTimestamps,
          yValues: values,
          yUnit: this.extractUnit(columnName) || 'unitless'
        };
        
        // Apply data quality cleanup
        const cleanupResult = cleanTimeSeriesData(curve);
        curves.push(cleanupResult.cleanedCurve);
      }
    }
    
    if (curves.length === 0) {
      throw new Error('No valid measurement data found');
    }
    
    return curves;
  }
  
  /**
   * Parse dual timestamp (separate date and time strings)
   */
  private static parseDualTimestamp(dateStr: string, timeStr: string): Date | null {
    try {
      // Handle common formats like "YYYY/MM/DD" + "HH:MM:SS"
      const combinedStr = `${dateStr} ${timeStr}`;
      const timestamp = new Date(combinedStr);
      
      if (!isNaN(timestamp.getTime())) {
        return timestamp;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }
  
  /**
   * Parse single timestamp string
   */
  private static parseSingleTimestamp(datetimeStr: string): Date | null {
    try {
      const timestamp = new Date(datetimeStr);
      
      if (!isNaN(timestamp.getTime())) {
        return timestamp;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }
  
  /**
   * Validate timestamp is within reasonable range
   */
  private static isValidTimestamp(timestamp: Date): boolean {
    const now = new Date();
    const minDate = new Date('2000-01-01');
    const maxDate = new Date(now.getTime() + 24 * 60 * 60 * 1000); // Allow 1 day in future
    
    return timestamp >= minDate && timestamp <= maxDate;
  }
  
  /**
   * Validate date string format
   */
  private static isValidDateString(dateStr: string): boolean {
    // Check for common date patterns
    const datePatterns = [
      /^\d{4}\/\d{1,2}\/\d{1,2}$/, // YYYY/MM/DD
      /^\d{4}-\d{1,2}-\d{1,2}$/,   // YYYY-MM-DD
      /^\d{1,2}\/\d{1,2}\/\d{4}$/  // MM/DD/YYYY
    ];
    
    return datePatterns.some(pattern => pattern.test(dateStr));
  }
  
  /**
   * Validate time string format
   */
  private static isValidTimeString(timeStr: string): boolean {
    // Check for common time patterns
    const timePatterns = [
      /^\d{1,2}:\d{2}:\d{2}$/,     // HH:MM:SS
      /^\d{1,2}:\d{2}$/,           // HH:MM
      /^\d{1,2}:\d{2}:\d{2}\.\d+$/ // HH:MM:SS.sss
    ];
    
    return timePatterns.some(pattern => pattern.test(timeStr));
  }
  
  /**
   * Validate datetime string format
   */
  private static isValidDateTimeString(datetimeStr: string): boolean {
    try {
      const timestamp = new Date(datetimeStr);
      return !isNaN(timestamp.getTime());
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Extract unit from column name (e.g., "Pressure (psi)" -> "psi")
   */
  private static extractUnit(columnName: string): string | null {
    const unitMatch = columnName.match(/\(([^)]+)\)$/);
    return unitMatch ? unitMatch[1] : null;
  }
  
  /**
   * Extract basic metadata
   */
  private static extractMetadata(filename: string, rowCount: number, columnCount: number): Record<string, string> {
    return {
      filename: filename || 'unknown',
      parser: 'GenericCsvParser',
      fileType: 'Generic CSV',
      totalRows: rowCount.toString(),
      totalColumns: columnCount.toString(),
      parseTime: new Date().toISOString()
    };
  }
}
